import React, { useCallback } from 'react';
import { Menu } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'shadcn/components/ui/DropdownMenu';

export const BATCH_SHIPMENT_STATUS_LABEL_PRINTED = 'LABEL_PRINTED';

/**
 * Overflow menu for View Batches list rows.
 *
 * @param {Object} props
 * @param {number | string} [props.batchId] - Batch id passed to action callbacks
 * @param {boolean} [props.canReverseFBMSubstitution=false] - Whether Reverse Substitution For Batch is available
 * @param {boolean} [props.isDownloading=false] - Whether this batch's print file is currently downloading
 * @param {(batchId: number | string) => void} [props.onApplySubstitutions] - Invoked when Update Substitutions is selected
 * @param {(batchId: number | string) => void} [props.onDownloadFile] - Invoked with the batch id when Download File is selected
 * @param {(batchId: number | string) => void} [props.onReverseSubstitution] - Invoked when Reverse Substitution For Batch is selected
 * @param {string | null | undefined} [props.shipmentStatus] - Batch shipment status; download requires `LABEL_PRINTED`
 * @returns {JSX.Element} Actions dropdown.
 */
export function BatchActionsMenu({
    batchId,
    canReverseFBMSubstitution = false,
    isDownloading = false,
    onApplySubstitutions,
    onDownloadFile,
    onReverseSubstitution,
    shipmentStatus,
}) {
    const hasBatchId = batchId !== undefined && batchId !== null && batchId !== '';
    const canDownloadFile = hasBatchId && !isDownloading && shipmentStatus === BATCH_SHIPMENT_STATUS_LABEL_PRINTED;

    const handleDownloadFile = useCallback(() => {
        if (!canDownloadFile) return;
        onDownloadFile?.(batchId);
    }, [batchId, canDownloadFile, onDownloadFile]);

    const handleApplySubstitutions = useCallback(() => {
        if (!hasBatchId) return;
        onApplySubstitutions?.(batchId);
    }, [batchId, hasBatchId, onApplySubstitutions]);

    const handleReverseSubstitution = useCallback(() => {
        if (!hasBatchId || !canReverseFBMSubstitution) return;
        onReverseSubstitution?.(batchId);
    }, [batchId, canReverseFBMSubstitution, hasBatchId, onReverseSubstitution]);

    const batchLabel = hasBatchId ? `batch ${batchId}` : 'batch';

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button type="button" variant="ghost" size="icon" aria-label={`Open actions menu for ${batchLabel}`}>
                    <Menu className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={handleDownloadFile} disabled={!canDownloadFile}>
                    {isDownloading ? 'Downloading file…' : 'Download print file'}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleApplySubstitutions} disabled={!hasBatchId}>
                    Update Substitutions
                </DropdownMenuItem>
                {canReverseFBMSubstitution ? (
                    <DropdownMenuItem onSelect={handleReverseSubstitution} disabled={!hasBatchId}>
                        Reverse Substitution For Batch
                    </DropdownMenuItem>
                ) : null}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

BatchActionsMenu.displayName = 'BatchActionsMenu';
